import { EDIT_SECTIONS, TOTAL_SECTIONS } from "./editSections";
import { ownershipTotal } from "./ownershipFields";
import { mergeSectionDrafts } from "./sectionDrafts";
import { SIGNATORY_SPEC, PERSONNEL_SPEC } from "./peopleSpecs";
import { liveTaxRows, isTaxRowComplete } from "./taxFields";
import { RELATED_ENTITY_SPEC, BUSINESS_RELATION_SPEC } from "./relationSpecs";
import { BANK_SPEC } from "./bankingFields";
import { INSURANCE_SPEC } from "./insuranceFields";
import { MANUAL_FIELD_KEYS } from "./financialSetupFields";
import { DOCUMENT_SPECS, hasDocument } from "./documentSpecs";

/* "15 of 19 sections complete" — where the numerator comes from.
 *
 * The denominator is real: 19 is the section count (editSections.js). The
 * NUMERATOR has no source. There is no completion field on the company record
 * and no endpoint that reports one — `getCompanyRequestStatus` reports the
 * admin REQUEST's approval state ("Completed", "Modified", …) for the company
 * as a whole, not per section, and the per-section `status` fields that do
 * exist (`industry.status`, `bankID.status`, `insuranceID.status`, …) are
 * approval states on nested records, present on some sections and absent on
 * others. See FINDINGS F-039.
 *
 * So rather than invent nineteen rules at once, each section declares a check
 * as its own UI is transcribed, and a section with NO check registered counts
 * as incomplete. That means the figure starts low and rises as the later
 * stages of TIP-644 land, which is honest: it never claims a section is
 * complete on a rule nobody has agreed.
 *
 * The live sidebar is no help here — its dots are positional
 * (`index <= activeStep` in EditSideBar.jsx), so they mark how far the viewer
 * has CLICKED, not what is filled in. The design's green dots clearly mean
 * something else, which is the same gap.
 */

/* Registered checks, keyed by the section's live label. Each reads the fields
   that section's own form reads, so the check and the form cannot drift apart.
   Stage 1 of TIP-644 covers About Business and Industry. */
/* Does this list section have a row the VIEWER can see? */
const hasRow = (company, spec, extras) =>
  mergeSectionDrafts(extras?.drafts?.[spec.key], company, spec).length > 0;

/* The same question for a SCALAR field, and the same answer. */
export const fieldWithDraft = (company, extras, key) => {
  const pending = extras?.companyDraft?.[key];
  if (pending !== null && pending !== undefined && pending !== "") return pending;
  return company?.[key];
};

export const SECTION_CHECKS = {
  /* About.jsx submits number_of_employees and company_description. */
  "About Business": (company, extras) =>
    Boolean(fieldWithDraft(company, extras, "number_of_employees")) &&
    hasText(fieldWithDraft(company, extras, "company_description")),

  /* Industry.jsx reads sub_industry_data and only reorders it — a record with
     industries has nothing left to answer. */
  Industry: (company) => (company?.sub_industry_data?.length || 0) > 0,

  /* AddressContact.jsx shows the AddButton only while the company has no
     address at all, so one undeleted address IS the section answered. The
     is_deleted filter is the same one the section renders through. */
  "Address & Contact Information": (company) =>
    (company?.companyAddress || []).some(
      (row) => Number(row?.company_address_detailsID?.is_deleted) === 0
    ),

  /* Social.jsx validates nothing and requires nothing, so there is no "done" to read off the form. */
  "Social Media": (company, extras) =>
    ["twitter", "facebook", "linkedin", "instagram", "website"].some((k) =>
      Boolean(fieldWithDraft(company, extras, k))
    ),

  /* LogoBanner.jsx uploads only the assets that changed and requires neither,
     so "answered" is both images present — a profile with a logo and no banner
     still has something to add. */
  "Logo & Banner": (company, extras) =>
    Boolean(fieldWithDraft(company, extras, "logo")) &&
    Boolean(fieldWithDraft(company, extras, "banner")),

  /* License Information is read-only and comes from the verified trade
     licence, so it is answered as soon as the record carries a licence
     number — there is nothing for the company to fill in. */
  "License Information": (company) => Boolean(company?.license_no),

  /* The section this rule was MISSING for, which is why its dot stayed grey however many registrations were on file: with no entry here isSectionComplete... */
  "Tax Information": (company) => liveTaxRows(company).some(isTaxRowComplete),

  /* OwnershipStructure.jsx only lets the flow move on at 0% or exactly 100%, so "answered" is a complete allocation — an ownership chart cannot be produced from... */
  "Ownership Structure": (company, extras) =>
    ownershipTotal(company, extras?.ownershipDrafts) === 100,

  /* The six list sections, all answered the same way: one row the viewer can see. */
  "Authorized Signatory": (company, extras) =>
    hasRow(company, SIGNATORY_SPEC, extras),
  "Key Personnel": (company, extras) => hasRow(company, PERSONNEL_SPEC, extras),
  "Related Entities": (company, extras) =>
    hasRow(company, RELATED_ENTITY_SPEC, extras),
  "Business Relation": (company, extras) =>
    hasRow(company, BUSINESS_RELATION_SPEC, extras),
  "Banking Information": (company, extras) => hasRow(company, BANK_SPEC, extras),
  Insurance: (company, extras) => hasRow(company, INSURANCE_SPEC, extras),

  /* One record, not a list. The firm is the section's subject, so a record
     that names one is the section answered — the dates and location are part
     of the same save and cannot be present without it. */
  "Auditor Information": (company) =>
    Boolean(company?.financialDetails?.[0]?.audit_firm_name),

  /* `financialDocument` is the company payload's per-year array, and the serializer returns one entry per year REGARDLESS — filled with nulls where there is... */
  "Financial Documents": (company) =>
    (company?.financialDocument || []).some((row) =>
      MANUAL_FIELD_KEYS.some(
        (key) => row?.[key] !== null && row?.[key] !== undefined && row?.[key] !== ""
      ) || Boolean(row?.document_url)
    ),

  /* ONE section for all three documents now, because one card holds all three slots. */
  ...DOCUMENT_SPECS.reduce(
    (checks, spec) => ({
      ...checks,
      [spec.section]: (company) =>
        DOCUMENT_SPECS.every((d) => hasDocument(company, d)),
    }),
    {}
  ),
};

/* Rich-text emptiness: ReactQuill stores "<p><br></p>" for an empty editor,
   which is truthy as a string but is not text. */
export const hasText = (value) => {
  if (typeof value !== "string") return Boolean(value);
  return value.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;
};

/* `extras` carries what a section fetches for ITSELF and the company record does not hold, so the rail can reach the same answer the section shows. */
export const isSectionComplete = (label, company, extras) => {
  const check = SECTION_CHECKS[label];
  /* No agreed rule yet — counted as incomplete rather than guessed. */
  if (!check) return false;
  try {
    return Boolean(check(company, extras));
  } catch {
    /* A shape we did not expect must not take the sidebar down with it; an
       unreadable section is simply not complete. */
    return false;
  }
};

export const completedSections = (company, extras) =>
  EDIT_SECTIONS.filter((s) => isSectionComplete(s.label, company, extras)).map(
    (s) => s.label
  );

export const completionSummary = (company, extras) => {
  const completed = completedSections(company, extras).length;
  return {
    completed,
    total: TOTAL_SECTIONS,
    /* Rounded, as the design shows a whole number. */
    percent: TOTAL_SECTIONS ? Math.round((completed / TOTAL_SECTIONS) * 100) : 0,
  };
};
